import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Button, InputField } from 'src/components/ui'
import { useForgotPasswordMutation, useResetPasswordMutation } from 'src/redux/api'
import { parsePhone } from 'src/utils/phone'
import AuthShell from 'src/components/elements/auth-shell/AuthShell'

const emailPattern = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/

// Email accounts get a reset link in the inbox; phone-only accounts have no
// inbox, so they confirm an SMS code and set the new password right here.
type Stage = 'request' | 'emailSent' | 'smsCode'

interface FormValues {
  emailOrPhone: string
  code: string
  password: string
  confirmPassword: string
}

const apiError = (error: unknown): { code?: string; message?: string } =>
  (error as { data?: { code?: string; message?: string } })?.data ?? {}

const ForgotPassword = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [stage, setStage] = useState<Stage>('request')
  const [submittedPhone, setSubmittedPhone] = useState('')
  const [forgotPassword, { isLoading: isRequesting }] = useForgotPasswordMutation()
  const [resetPassword, { isLoading: isResetting }] = useResetPasswordMutation()

  const {
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { emailOrPhone: '', code: '', password: '', confirmPassword: '' },
  })

  const submitRequest = async (data: FormValues) => {
    const parsed = parsePhone(data.emailOrPhone)
    try {
      await forgotPassword({ emailOrPhone: parsed?.e164 ?? data.emailOrPhone }).unwrap()
      if (parsed) {
        setSubmittedPhone(parsed.e164)
        setStage('smsCode')
      } else {
        setStage('emailSent')
      }
    } catch (error) {
      setError('root', {
        message: apiError(error).message || t('reset_password.unexpected'),
      })
    }
  }

  const submitSms = async (data: FormValues) => {
    try {
      await resetPassword({
        phoneNumber: submittedPhone,
        code: data.code.trim(),
        password: data.password,
      }).unwrap()
      toast.success(t('reset_password.success'))
      navigate('/login')
    } catch (error) {
      const { code, message } = apiError(error)
      setError('root', {
        message:
          code === 'RESET_INVALID'
            ? t('reset_password.reset_invalid')
            : message || t('reset_password.unexpected'),
      })
    }
  }

  const onSubmit = stage === 'smsCode' ? submitSms : submitRequest

  return (
    <AuthShell title={t('reset_password.title')}>
      {stage === 'emailSent' ? (
        <div className="flex flex-col gap-2 p-5 rounded-2xl bg-success-soft">
          <h2 className="m-0 text-lg font-extrabold leading-6 text-success">
            {t('reset_password.email_sent_title')}
          </h2>
          <p className="m-0 text-base leading-6 text-ink">{t('reset_password.email_sent_text')}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 self-stretch w-full">
          {errors.root?.message && (
            <p className="m-0 px-3.5 py-2.5 rounded-xl bg-danger-soft text-sm font-bold text-danger">
              {errors.root?.message}
            </p>
          )}
          <div className="flex flex-col gap-4">
            {stage === 'request' ? (
              <>
                <p className="m-0 text-base leading-6 text-ink-2">
                  {t('reset_password.request_hint')}
                </p>
                <Controller
                  name="emailOrPhone"
                  control={control}
                  rules={{
                    required: t('login.requiredField'),
                    validate: (value) => {
                      const stripped = value.replaceAll(' ', '')
                      if (!emailPattern.test(stripped) && parsePhone(stripped) === null) {
                        return t('login.emailOrPhoneError')
                      }
                      return true
                    },
                  }}
                  render={({ field }) => (
                    <InputField
                      {...field}
                      label={t('login.emailOrPhone')}
                      error={errors.emailOrPhone}
                    />
                  )}
                />
              </>
            ) : (
              <>
                <p className="m-0 text-base leading-6 text-ink-2">
                  {t('reset_password.sms_sent_hint', { phone: submittedPhone })}
                </p>
                <Controller
                  name="code"
                  control={control}
                  rules={{ required: t('reset_password.code_required') }}
                  render={({ field }) => (
                    <InputField
                      {...field}
                      label={t('reset_password.code')}
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      error={errors.code}
                    />
                  )}
                />
                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: t('login.requiredField'),
                    minLength: { value: 6, message: t('login.passwordMinLengthError') },
                  }}
                  render={({ field }) => (
                    <InputField
                      {...field}
                      label={t('reset_password.new_password')}
                      type="password"
                      autoComplete="new-password"
                      error={errors.password}
                    />
                  )}
                />
                <Controller
                  name="confirmPassword"
                  control={control}
                  rules={{
                    required: t('login.requiredField'),
                    validate: (value) =>
                      value === watch('password') || t('reset_password.passwords_mismatch'),
                  }}
                  render={({ field }) => (
                    <InputField
                      {...field}
                      label={t('reset_password.confirm_password')}
                      type="password"
                      autoComplete="new-password"
                      error={errors.confirmPassword}
                    />
                  )}
                />
              </>
            )}
          </div>
          <Button
            size="lg"
            className="w-full"
            type="submit"
            isLoading={isRequesting || isResetting}
          >
            {stage === 'request' ? t('reset_password.submit_request') : t('reset_password.save')}
          </Button>
        </form>
      )}
      <Link
        to="/login"
        className="self-center min-h-11 inline-flex items-center font-bold text-brand-ink hover:underline"
      >
        {t('reset_password.back_to_login')}
      </Link>
    </AuthShell>
  )
}

export default ForgotPassword
